import { createContext, useContext, useState, ReactNode } from 'react';

export type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>('dark');

  const toggleTheme = () => setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  const setTheme = (t: Theme) => setThemeState(t);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export const themeColors = {
  dark: {
    bg: 'bg-[#08090F]',
    bgCard: 'bg-[#111827]',
    bgCardHover: 'hover:bg-[#1a2234]',
    bgSecondary: 'bg-[#0D1117]',
    bgInput: 'bg-[#1a1f2e]',
    bgModal: 'bg-[#0f1523]',
    border: 'border-[#1e2d47]',
    borderGlow: 'border-[#00d4ff]/40',
    text: 'text-white',
    textMuted: 'text-gray-400',
    textSecondary: 'text-gray-300',
    accent: 'text-[#00d4ff]',
    accentBg: 'bg-[#00d4ff]',
    accentSoft: 'bg-[#00d4ff]/10',
    accent2: 'text-[#a78bfa]',
    accent2Bg: 'bg-[#a78bfa]',
    accent2Soft: 'bg-[#a78bfa]/10',
    navBg: 'bg-[#0D1117]/95',
    navBorder: 'border-[#1e2d47]',
    badge: 'bg-[#00d4ff]/20 text-[#00d4ff]',
    badgePurple: 'bg-[#a78bfa]/20 text-[#a78bfa]',
    badgeGold: 'bg-amber-500/20 text-amber-400',
    gradientHero: 'from-[#08090F] via-[#0d1117] to-[#08090F]',
    glow: 'shadow-[0_0_20px_rgba(0,212,255,0.15)]',
    glowStrong: 'shadow-[0_0_30px_rgba(0,212,255,0.3)]',
  },
  light: {
    bg: 'bg-[#F8F9FB]',
    bgCard: 'bg-white',
    bgCardHover: 'hover:bg-gray-50',
    bgSecondary: 'bg-[#F0F2F8]',
    bgInput: 'bg-white',
    bgModal: 'bg-white',
    border: 'border-gray-200',
    borderGlow: 'border-amber-400/60',
    text: 'text-gray-900',
    textMuted: 'text-gray-500',
    textSecondary: 'text-gray-600',
    accent: 'text-[#0070E0]',
    accentBg: 'bg-[#0070E0]',
    accentSoft: 'bg-[#0070E0]/10',
    accent2: 'text-amber-600',
    accent2Bg: 'bg-amber-500',
    accent2Soft: 'bg-amber-500/10',
    navBg: 'bg-white/95',
    navBorder: 'border-gray-200',
    badge: 'bg-blue-100 text-blue-700',
    badgePurple: 'bg-purple-100 text-purple-700',
    badgeGold: 'bg-amber-100 text-amber-700',
    gradientHero: 'from-[#F8F9FB] via-[#EEF2FF] to-[#F8F9FB]',
    glow: 'shadow-[0_4px_20px_rgba(0,112,224,0.12)]',
    glowStrong: 'shadow-[0_4px_30px_rgba(0,112,224,0.2)]',
  }
};
